import { createContext, useContext, type ReactNode } from "react";
import { useAuthRedux } from "./use-auth-redux";

export type UserRole = "auditor" | "manager" | "admin";

export interface User {
  id?: string;
  _id?: string;
  email: string;
  name: string;
  role: UserRole;
  password?: string;
  createdAt?: string;
  phone?: string;
  notificationPreferences?: Record<string, boolean>;
  applicationPreferences?: {
    theme?: "light" | "dark" | "system";
    [key: string]: unknown;
  };
}

interface AuthContextType {
  user: User | null;
  login: (credentials: { email: string; password: string }) => Promise<unknown>;
  logout: () => Promise<void>;
  error?: string | null;
  clearError?: () => void;
  isLoading: boolean;
  token: string | null;
  passwordChangeSuccess: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const {
    user,
    login,
    logout,
    isLoading,
    error,
    clearError,
    passwordChangeSuccess,
  } = useAuthRedux();

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isLoading,
        error,
        clearError,
        token: null,
        passwordChangeSuccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
